/**
 * @file Contains some general util functions
 * @author Haonan Yu(haoyu@microStrategy.com)
 */

import React from 'react';
import 'antd/dist/antd.css';

/**
 *Get the value of a state and set the value to a specific field in the current form page
 * @param {string} fieldName the field you want to set
 * @param {string} stateName the state that you want to get value from and put the value to the field
 */
export const setFormFieldValue = (fieldName, stateName, props) => {
    const { setFieldsValue, getFieldValue } = props.form;
    if (getFieldValue(fieldName) !== props.getOriginState(stateName)) {
        setFieldsValue({
            [fieldName]: props.getOriginState(stateName),
        });
    }
};

/**
 * Generate a tree-like summary list based on a specific structural data
 * The output jsx will be like
 * <ul>
 *    <li>
 *        <ul>
 *          <li>
 *              ...
 *          </li>
 *        </ul>
 *    </li>
 *    ...
 * </ul>
 * @param {Array} sumFrame an array that follow the structure to generate Summary content
 * @param {Object} props an object passed with some necessary methods
 * to communicate with states in the higher component level
 * @return {Object} an jsx object contains a tree-like structure
 */
export const generateSummary = (sumFrame, props) => {
    if (sumFrame === undefined || sumFrame === null || sumFrame.length === 0) {
        return null;
    }
    let content = [];
    for (let i = 0; i < sumFrame.length; i++) {
        let item = sumFrame[i];

        // corner case for Linux Topology display
        if (
            item.state === 'servicesRegistrationServerHosts' &&
            props.getOriginState('servicesRegistrationMachineEnvironment') ===
                'SINGLE'
        ) {
            continue;
        }

        if (
            item.label === 'Intelligence Service Account' &&
            props.getOriginState('isSkipAccountSetting')
        ) {
            continue;
        }

        if (
            item.label === 'Narrowcast Server Account' &&
            props.getOriginState('nsSkipAccountSetting')
        ) {
            continue;
        }

        if (checkDependencyStates(item.visibleDependStates, props)) {
            content.push(
                <li key={item.label}>
                    {' '}
                    {item.label} {printState(item.state, props)}
                    {generateSummary(item.children, props)}
                </li>
            );
        }
    }
    if (content.length === 0) {
        return null;
    }

    return <ul>{content}</ul>;
};

/**
 * Get the value of a state of higher level component and output it as String type
 * @param {String} state the name of the state get value from
 * @param {Object} props an object passed with some necessary methods
 * to communicate with states in the higher component level
 * @returns {String} return the value of a state as String
 */
const printState = (state, props) => {
    let value = props.getOriginState(state);
    if (value === undefined || value === null) {
        return null;
    }

    /*Handle the initial ["", ""] case. If has one "", the array must be the initial placeholder
      and should not be displayed in Summary
      */
    if (Array.isArray(value) && value.includes('')) {
        return '';
    }

    return typeof value === 'string' ? value : value.toString();
};

/**
 * Return true either the stateArray is empty or has at least one state
 * which status is true
 * @param {Array} stateArray an array that contains states to check,
 * the state must be available and are boolean type
 * @param {Object} props an object passed with some necessary methods
 * to communicate with states in the higher component level
 * @return {boolean}
 * true, if the stateArray is empty or has at least one state
 * which status is true
 * false, otherwise
 */
const checkDependencyStates = (stateArray, props) => {
    if (stateArray.length === 0) {
        return true;
    }
    for (let i = 0; i < stateArray.length; i++) {
        let curState = stateArray[i];
        if (
            props.getOriginState(curState) === undefined ||
            props.getOriginState(curState) === null
        ) {
            return false;
        }
        if (props.getOriginState(curState)) {
            return true;
        }
    }

    return false;
};

/**
 * generate Array that contains configuration page JSX based on the features that user select to install
 * if a product is not selected, it's configuration page should not display
 * @param {Object} pageObjects object that contains information for each page
 * @param {Array} pageArray Array that contains all the pages' JSX
 * @param {Object} props an object passed with some necessary methods to communicate with states in the higher component level
 * @return {Array} return the array that contains necessary configuration page JSX
 */
export const generateSteps = (pageObjects, pageArray, props) => {
    let res = [];
    pageObjects.forEach((item) => {
        //the page should be displayed if it has no dependent product or any of its dependent product is chosen
        let dependStates = item.dependOnState;
        if (dependStates.length === 0) {
            res.push(pageArray[item.id]);
        } else
            for (let i = 0; i < dependStates.length; i++) {
                if (dependStates[i].startsWith('!')) {
                    // if the state is false, then display the page
                    let stateString = dependStates[i].substring(1).trim();
                    if (!props.getOriginState(stateString)) {
                        res.push(pageArray[item.id]);
                        break;
                    }
                } else {
                    // if the state is true, then display the page
                    if (props.getOriginState(dependStates[i])) {
                        res.push(pageArray[item.id]);
                        break;
                    }
                }
            }
    });
    return res;
};

/**
 * Upload a file to AWS S3 bucket with the name in format of "Date" + "Company Name" + "File Type"
 * @param {String} url the url of the AWS S3 Bucket to upload file
 * @param {File} data the file to upload to the Bucket
 * @param {Object} info an Object contains external information to lambda function
 * @callback successCB if success, call this function
 * @callback errorCB if error, call this function
 */
export const triggerLambda = (info, successCB, errorCB) => {
    let url =
        'https://91fi9np5sl.execute-api.us-east-2.amazonaws.com/dev/response-file-upload';
    let xhr = new XMLHttpRequest();
    let d = new Date();

    xhr.addEventListener('load', function () {
        if (xhr.status === 200) {
            successCB(xhr.response);
        } else {
            errorCB({
                status: xhr.status,
                response: xhr.response,
            });
        }
    });

    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('X-Custom-Day', d.getDate());
    xhr.setRequestHeader('X-Custom-Month', d.getMonth() + 1);
    xhr.setRequestHeader('X-Custom-Year', d.getFullYear());
    xhr.setRequestHeader('X-Custom-Hours', d.getHours());
    xhr.setRequestHeader('X-Custom-Minutes', d.getMinutes());
    xhr.setRequestHeader('X-Custom-Seconds', d.getSeconds());
    xhr.setRequestHeader('X-Custom-FileName', info.fileName);

    xhr.setRequestHeader('X-Custom-CompanyName', info.companyName);
    xhr.setRequestHeader('X-Custom-OS', info.os);
    xhr.setRequestHeader('X-Custom-Mode', info.mode);
    xhr.setRequestHeader('X-Custom-Version', info.version);

    xhr.send();
};
